/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { format } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';

import { navigate } from 'gatsby';

import { PergoTextField } from 'components/PergoTextField/PergoTextField';
import { CallContext } from 'context/callContextContainer';
import { jobsService } from 'services/jobsService';
import { logout } from 'services/loginService';

const moment = require('moment-timezone');

/**
 * Dispatch board "Expired" tab.
 *
 * Lists jobs that platform-scripts/expire-jobs-cron.php aged out (status 12):
 * jobs left at CFS / dispatched / mid-trip past their scheduled time or last
 * milestone. Read-only — a manager restores a job by clicking its Job # and
 * saving the reservation with a future pickup time (the editor and save_job
 * both reject a past time), or marks a forgotten trip Complete from the manage
 * Jobs page. No polling: the list loads when the tab is opened, whenever the
 * window / page / sort / search changes, and when the parent bumps refreshKey
 * (it does so when the job editor closes).
 */

// Window on expired_time. Keys are what get_expired_jobs accepts.
const WINDOW_OPTIONS = [
  { value: '1w', label: 'Last week' },
  { value: '2w', label: 'Last 2 weeks' },
  { value: '3w', label: 'Last 3 weeks' },
  { value: '4w', label: 'Last 4 weeks' },
  { value: '2m', label: 'Last 2 months' },
  { value: '3m', label: 'Last 3 months' },
];

// Status the job had when it expired (jobs.expired_from_status).
const ORIGINAL_STATUS = {
  0: 'CFS',
  1: 'Dispatched',
  2: 'Accepted',
  4: '@Pickup',
  5: 'Picked Up',
  6: 'Drop-Off',
  7: 'Pay',
  11: 'Backlog',
};

const headCells = [
  { id: 'job_id', label: 'Job #' },
  { id: 'driver', label: 'Driver' },
  { id: 'passenger', label: 'Passenger' },
  { id: 'pickup', label: 'Pickup' },
  { id: 'dropoff', label: 'Drop Off' },
  { id: 'time', label: 'Scheduled Time' },
  { id: 'status', label: 'Original Status' },
  { id: 'broker', label: 'Broker', sortable: false },
  { id: 'expired', label: 'Expired Time' },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell key={headCell.id} align="center" sortDirection={orderBy === headCell.id ? order : false}>
            {headCell.sortable === false ? (
              headCell.label
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const ExpiredTable = ({ editJob, isActive, isUpdate, refreshKey, isCallCenter }) => {
  const [tableData, setTableData] = useState([]);
  const [size, setSize] = useState(0);
  // Distinguish "nothing expired" from "could not load": an empty list here is
  // a business conclusion, so a failed fetch must not look like one.
  const [loadError, setLoadError] = useState('');
  const [info, setInfo] = useState({
    order: 'desc',
    orderBy: 'expired',
    page: 0,
    rowsPerPage: 20,
    window: '1w',
    search: [],
    searchValues: {},
  });
  const { siteSetting, callcenterActiveDb } = useContext(CallContext);

  const formatSiteTime = (utcValue, fmt = 'MM/DD/YYYY hh:mm A') =>
    utcValue && moment(utcValue).isValid()
      ? moment
          .utc(utcValue)
          .tz(siteSetting().timezone)
          .format(fmt)
      : '';

  const jobTableRowFormat = row => {
    const fromStatus = parseInt(row.expired_from_status);
    let original_status = ORIGINAL_STATUS[fromStatus] || (Number.isNaN(fromStatus) ? '' : String(fromStatus));
    if (fromStatus === 0 && parseInt(row.job_type) === 2) {
      original_status = 'Reservation';
    }

    return {
      ...row,
      job_id: row.id,
      driver: parseInt(row.driver_id) > 0 ? `${row.driver_fname || ''} ${row.driver_lname || ''}`.trim() : '',
      passenger: `${row.passenger_fname || ''} ${row.passenger_lname || ''}`.trim(),
      pickup: row.pickup
        ? [row.pickup.location_name, row.pickup.addr1, row.pickup.city, row.pickup.state]
            .filter(str => str?.length > 0)
            .join(', ')
        : row.start_addr,
      dropoff: row.dropoff
        ? [row.dropoff.location_name, row.dropoff.addr1, row.dropoff.city, row.dropoff.state]
            .filter(str => str?.length > 0)
            .join(', ')
        : row.end_addr,
      time: formatSiteTime(row.reservation_time, String(row.willcall_status) === '1' ? 'MM/DD/YYYY' : 'MM/DD/YYYY hh:mm A'),
      original_status,
      // Broker-billed legs need a manager to Complete / No-Show / Cancel them
      // before attestation; make them stand out.
      broker: [row.mas_invoice_number ? 'MAS' : null, row.kinetik_trip_id ? 'Kinetik' : null]
        .filter(Boolean)
        .join(' / '),
      expired: formatSiteTime(row.expired_time),
    };
  };

  const updateTable = async () => {
    if (isCallCenter && !callcenterActiveDb) return;
    try {
      const res = await jobsService.get_expired_jobs({
        window: info.window,
        order_by: info.orderBy,
        order: info.order,
        search: JSON.stringify(info.search),
        page_size: info.rowsPerPage,
        page_num: info.page,
        ...(isCallCenter && { dbname: callcenterActiveDb }),
      });
      if (res?.data?.status === 'SUCCESS') {
        setTableData((res.data.data.data || []).map(item => jobTableRowFormat(item)));
        setSize(parseInt(res.data.data.cnt, 10) || 0);
        setLoadError('');
      } else if (res?.data?.status === 'FAIL' && res.data.data?.code === 'NOT_LOGGED_IN') {
        logout();
        navigate('/login');
      } else {
        setTableData([]);
        setSize(0);
        setLoadError(`Could not load expired jobs: ${res?.data?.data?.msg || 'unexpected response'}`);
      }
    } catch (e) {
      setTableData([]);
      setSize(0);
      setLoadError('Could not load expired jobs: network or server error');
    }
  };

  // Load on tab open, on every filter change, and when the parent bumps
  // refreshKey (job editor closed). No interval — expired jobs only change when
  // the hourly cron runs or a manager restores one.
  useEffect(() => {
    if (isActive) {
      updateTable();
    }
  }, [
    isActive,
    isUpdate,
    refreshKey,
    info.window,
    info.order,
    info.orderBy,
    info.page,
    info.rowsPerPage,
    info.search,
    callcenterActiveDb,
  ]);

  const handleRequestSort = (event, property) => {
    const isAsc = info.orderBy === property && info.order === 'asc';
    setInfo({ ...info, order: isAsc ? 'desc' : 'asc', orderBy: property, page: 0 });
  };

  const handleChangePage = (event, newPage) => {
    setInfo({ ...info, page: newPage });
  };

  const handleChangeRowsPerPage = event => {
    setInfo({ ...info, rowsPerPage: parseInt(event.target.value, 10), page: 0 });
  };

  const handleWindowChange = event => {
    setInfo({ ...info, window: event.target.value, page: 0 });
  };

  const handleSearchClick = searchValues => {
    setInfo({
      ...info,
      page: 0,
      search: [
        { id: 'fname', value: searchValues.fname || '' },
        { id: 'lname', value: searchValues.lname || '' },
        { id: 'phone', value: searchValues.phone || '' },
        { id: 'reservation_date', value: searchValues.reservation_date || '' },
      ],
      searchValues,
    });
  };

  return (
    <TableContainer>
      <TableSearch>
        <TableSearchGroup
          values={info.searchValues}
          window={info.window}
          onWindowChange={handleWindowChange}
          onSearchClick={handleSearchClick}
        />

        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={size}
          rowsPerPage={info.rowsPerPage}
          page={info.page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </TableSearch>

      <Table aria-label="expired job table">
        <EnhancedTableHead order={info.order} orderBy={info.orderBy} onRequestSort={handleRequestSort} />

        <TableBody>
          {tableData.map((row, index) => (
            <TableRow className="job-status-expired" hover tabIndex={-1} key={`${row.job_id}_${index}`}>
              <TableCell
                align="center"
                component="th"
                scope="row"
                padding="none"
                style={{ cursor: 'pointer' }}
                onClick={() => editJob(row.job_id)}
              >
                {row.job_id}
              </TableCell>
              <TableCell align="center">{row.driver}</TableCell>
              <TableCell align="center">{row.passenger}</TableCell>
              <TableCell align="center">{row.pickup}</TableCell>
              <TableCell align="center">{row.dropoff}</TableCell>
              <TableCell align="center">{row.time}</TableCell>
              <TableCell align="center">{row.original_status}</TableCell>
              <TableCell align="center" style={row.broker ? { fontWeight: 600, color: '#b45309' } : undefined}>
                {row.broker}
              </TableCell>
              <TableCell align="center">{row.expired}</TableCell>
            </TableRow>
          ))}
          {tableData.length === 0 && (
            <TableRow>
              <TableCell
                align="center"
                colSpan={headCells.length}
                style={{ color: loadError ? '#c0392b' : '#666', padding: '20px' }}
              >
                {loadError || 'No expired jobs in this window.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component="div"
        count={size}
        rowsPerPage={info.rowsPerPage}
        page={info.page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};

ExpiredTable.propTypes = {
  editJob: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  isUpdate: PropTypes.bool,
  refreshKey: PropTypes.number,
  isCallCenter: PropTypes.bool,
};

ExpiredTable.defaultProps = {
  isActive: true,
  isUpdate: false,
  refreshKey: 0,
  isCallCenter: false,
};

export default ExpiredTable;

const TableSearchGroup = ({ values, window: windowValue, onWindowChange, onSearchClick }) => {
  const [searchValues, setSearchValues] = useState({ ...values });

  const onSearchChange = (id, newValue) => {
    let tmpValue = newValue;
    if (id === 'reservation_date') {
      try {
        tmpValue = newValue ? format(newValue, 'yyyy-MM-dd') : '';
      } catch {
        tmpValue = '';
      }
    }
    setSearchValues({ ...searchValues, [id]: tmpValue });
  };

  return (
    <div className="expired-table-search--container">
      <FormControl variant="outlined" className="expired-table-window">
        <InputLabel id="expired-window-label">Expired Within</InputLabel>
        <Select labelId="expired-window-label" label="Expired Within" value={windowValue} onChange={onWindowChange}>
          {WINDOW_OPTIONS.map(opt => (
            <MenuItem value={opt.value} key={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <PergoTextField id="fname" values={searchValues} handleChange={onSearchChange} label="First Name" />
      <PergoTextField id="lname" values={searchValues} handleChange={onSearchChange} label="Last Name" />
      <PergoTextField id="phone" values={searchValues} handleChange={onSearchChange} label="Phone" />
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <KeyboardDatePicker
          inputVariant="outlined"
          format="yyyy/MM/dd"
          label="Scheduled Date"
          clearable
          value={searchValues?.reservation_date ? moment(searchValues.reservation_date).toDate() : null}
          onChange={e => onSearchChange('reservation_date', e)}
        />
      </MuiPickersUtilsProvider>
      <Button className="expired-table-search--button" onClick={() => onSearchClick?.(searchValues)}>
        Search
      </Button>
    </div>
  );
};

const TableSearch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;

  .expired-table-search--container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding-top: 5px;

    .MuiTextField-root,
    .expired-table-window {
      margin-right: 10px;
    }

    .expired-table-window {
      min-width: 170px;

      .MuiSelect-outlined {
        padding: 10px 32px 10px 10px;
        font-size: 14px;
      }
    }

    .MuiInputLabel-outlined {
      font-size: 14px;
      transform: translate(14px, 12px) scale(1);
      background: transparent;
      padding: 0 3px;

      &.MuiInputLabel-shrink {
        transform: translate(14px, -5px) scale(0.75);
      }
    }

    .MuiOutlinedInput-input {
      padding: 10px;
      font-size: 14px;
      height: auto;
    }

    .expired-table-search--button {
      border-radius: 0;
      background: var(--blue-main-color);
      color: white;
      height: 39px;
    }
  }
`;
